import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdStar, MdCheckCircle } from 'react-icons/md';
import { FaBolt } from 'react-icons/fa';

function SpotlightPollCard() {
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answering, setAnswering] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchSpotlight = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/spotlight-poll`);
      setSpotlight(res.data.spotlight);
    } catch {
      setSpotlight(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpotlight();
  }, [fetchSpotlight]);

  const handleVote = async (answer) => {
    if (answering || answered) return;
    setAnswering(true);
    setSelectedAnswer(answer);
    try {
      await axios.post(`${API_URL}/api/spotlight-poll/${spotlight.customPollId}/respond`, {
        answer,
      });
      setAnswered(true);
      // Collapse after showing the success state — no page reload
      setTimeout(() => {
        setCollapsed(true);
      }, 2500);
    } catch (err) {
      console.error('Spotlight vote error:', err);
      setAnswering(false);
      setSelectedAnswer(null);
    }
  };

  if (loading || !spotlight || collapsed) return null;

  return (
    <div
      className={`mb-8 rounded-2xl overflow-hidden shadow-xl transition-all duration-500 ${
        answered ? 'max-h-32 opacity-80' : 'max-h-[500px]'
      }`}
      style={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #ec4899 100%)',
      }}
      data-testid="spotlight-poll-card"
    >
      {answered ? (
        <div className="p-6 flex items-center justify-center gap-3 text-white" data-testid="spotlight-answered-state">
          <MdCheckCircle className="text-3xl" />
          <div>
            <p className="font-bold font-rubik text-lg">Thanks for voting!</p>
            <p className="text-white/90 font-rubik text-sm flex items-center gap-1">
              <FaBolt className="text-yellow-300" />
              +{spotlight.bonusPoints} bonus points earned
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="px-6 pt-5 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
              <MdStar className="text-yellow-300 text-lg" />
              <span className="text-white font-bold text-xs uppercase tracking-wider font-rubik">
                Spotlight Poll
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/25 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <FaBolt className="text-yellow-300 text-sm" />
              <span className="text-white font-bold text-sm font-rubik">
                +{spotlight.bonusPoints} pts
              </span>
            </div>
          </div>

          <div className="px-6 pb-4">
            <h3
              className="text-xl sm:text-2xl font-bold text-white font-rubik leading-snug"
              data-testid="spotlight-question"
            >
              {spotlight.question}
            </h3>
          </div>

          <div className="px-6 pb-6 flex gap-3">
            <button
              onClick={() => handleVote('yes')}
              disabled={answering}
              className={`flex-1 py-3.5 rounded-xl font-bold text-lg font-rubik transition-all ${
                answering && selectedAnswer === 'yes'
                  ? 'bg-white text-green-600 scale-95'
                  : 'bg-white/20 hover:bg-white/30 text-white border border-white/30 hover:border-white/50'
              } disabled:cursor-not-allowed`}
              data-testid="spotlight-vote-yes"
            >
              {answering && selectedAnswer === 'yes' ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                  Voting...
                </span>
              ) : (
                'Yes'
              )}
            </button>
            <button
              onClick={() => handleVote('no')}
              disabled={answering}
              className={`flex-1 py-3.5 rounded-xl font-bold text-lg font-rubik transition-all ${
                answering && selectedAnswer === 'no'
                  ? 'bg-white text-red-600 scale-95'
                  : 'bg-white/20 hover:bg-white/30 text-white border border-white/30 hover:border-white/50'
              } disabled:cursor-not-allowed`}
              data-testid="spotlight-vote-no"
            >
              {answering && selectedAnswer === 'no' ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                  Voting...
                </span>
              ) : (
                'No'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default SpotlightPollCard;
